import { useRef } from "react";
import { Typography } from "@/components/atoms";
import { ClientBookingQuote } from "@/redux/apis/clientBooking/types";
import { clsx, formatAud } from "@/utils";
import { useBookingViewOnly } from "../BookingViewOnlyContext";
import { PriceSummary, PriceSummaryRow } from "../confirmation/PriceSummary";
import { TotalPriceOverride } from "./TotalPriceOverride";

/** The missing-field label the fixed-rate price gate contributes (bookingPayload.getMissingBookingFields). */
const FIXED_PRICE_FIELD = "Fixed price";

/** "a, b and c" — for the missing-fields hints here and under the Update Booking CTA. */
export const listify = (items: string[]) =>
  items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

/**
 * Rows the quote provides: journey cost + extras, then the itemized fees
 * (Service Fee, Credit Card Processing Fee) as their own divider-separated
 * group per Figma. processing_fees is absent until the backend change deploys,
 * so the fee group degrades away cleanly.
 */
/** @deprecated Use `formatAud` from `@/utils`. */
export const fmt = formatAud;

export const toGroups = (quote: ClientBookingQuote): PriceSummaryRow[][] => {
  const rows: PriceSummaryRow[] = [{ label: "Journey Cost", value: formatAud(quote.vehicles.cost) }];

  const extras = quote.vehicles.components ?? [];
  if (extras.length > 0) {
    rows.push({ label: "Extras" });
    extras.forEach((extra) => rows.push({ label: extra.name, value: `+ ${formatAud(extra.cost)}`, indent: true }));
  }

  const fees = (quote.processing_fees?.components ?? []).map((fee) => ({ label: fee.name, value: `+ ${formatAud(fee.cost)}` }));

  return fees.length > 0 ? [rows, fees] : [rows];
};

interface QuotePriceSummaryProps {
  quote?: ClientBookingQuote;
  isQuotable: boolean;
  isLoading: boolean;
  missingFields: string[];
  canOverridePrice?: boolean;
  preOverrideTotal?: number;
  className?: string;
}

/**
 * Price Summary section of the priced Booking Summary. Live quote rows when the
 * draft is quotable; skeleton while quoting; a muted "what's left" hint otherwise.
 */
export const QuotePriceSummary = ({
  quote,
  isQuotable,
  isLoading,
  missingFields,
  canOverridePrice,
  preOverrideTotal,
  className,
}: QuotePriceSummaryProps) => {
  const viewOnly = useBookingViewOnly();
  const returnFocusRef = useRef(false);

  // The operator can hide pricing from an invoice client — the backend nulls every cost/total and sets
  // is_pricing_available=false. Match the legacy sheet: omit the whole price summary rather than render
  // a misleading $0 for every row. Operators booking OBO always get is_pricing_available=true (they may
  // still set the price), so this only ever hides for the actual client. (undefined = older backend =
  // allowed, so gate strictly on `=== false`.)
  if (quote?.is_pricing_available === false) return null;

  if (isQuotable && quote && !isLoading) {
    return (
      <PriceSummary
        groups={toGroups(quote)}
        total={formatAud(quote.total)}
        totalRow={
          canOverridePrice ? (
            <TotalPriceOverride total={Number(quote.total)} preOverrideTotal={preOverrideTotal} returnFocusRef={returnFocusRef} />
          ) : undefined
        }
        className={className}
      />
    );
  }

  // View-only with no stored price (e.g. a no-cost closed booking): there's nothing to show and no
  // action to prompt — omit the section entirely rather than showing a "complete X to see pricing" hint.
  if (viewOnly) return null;

  if (canOverridePrice && !isLoading && missingFields.includes(FIXED_PRICE_FIELD)) {
    const others = missingFields.filter((field) => field !== FIXED_PRICE_FIELD);
    return (
      <div className={clsx("flex flex-col", className)}>
        <PriceSummary groups={[]} totalRow={<TotalPriceOverride returnFocusRef={returnFocusRef} />} />
        {others.length > 0 && (
          <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray">
            {`Complete ${listify(others.map((field) => field.toLowerCase()))} to see the full price`}
          </Typography>
        )}
      </div>
    );
  }

  // Loading: show ONLY the skeleton — no "Price Summary" heading. A hidden-pricing client's quote
  // resolves to is_pricing_available=false and the whole section is then removed; rendering the heading
  // here first makes it flash in and vanish. A title-shaped pulse bar keeps the skeleton reading as the
  // price block without committing a real header (which appears with the rows once the quote lands).
  if (isLoading) {
    return (
      <div className={clsx("flex animate-pulse flex-col gap-2", className)} role="status" aria-label="Loading price">
        <div className="h-6 w-1/3 rounded-2xl bg-neutral-gray" />
        <div className="h-4 rounded-2xl bg-neutral-gray" />
        <div className="h-4 rounded-2xl bg-neutral-gray" />
        <div className="h-6 w-1/2 self-end rounded-2xl bg-neutral-gray" />
      </div>
    );
  }

  return (
    <div className={clsx("flex flex-col", className)}>
      <Typography variant="title">Price Summary</Typography>
      <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray">
        {missingFields.length > 0
          ? `Complete ${listify(missingFields.map((field) => field.toLowerCase()))} to see pricing`
          : "Pricing is unavailable right now"}
      </Typography>
    </div>
  );
};
